import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

import { SoftIcon } from './ui/SoftIcon';
import { MemberInfoCard } from './parts/MemberInfoCard';
import { HealthNotesCard } from './parts/HealthNotesCard';
import { PlanPickerCard } from './parts/PlanPickerCard';
import { MemberSummaryCard } from './parts/MemberSummaryCard';
import { MembershipUsageCard } from './MembershipUsageCard';
import { FieldCard } from './ui/FieldCard';
import { TrainerSelectCard } from './parts/TrainerSelectCard';
import { money } from 'sectors/pilates/members/components/paymentReceive/ui';
import { uiBus } from 'shared/api/uiBus';

const emptyForm = () => ({
  name: '',
  surname: '',
  birthDate: '',
  phone: '',
  notes: '',

  planRef: '',
  customPlan: null,

  planSnapshot: null,
  planPriceTry: null,
  agreedPriceTry: '',

  paidTry: '',

  membershipStatus: true,
  startAt: '',

  usageByItem: [],

  communicationConsent: {
    sms: true,
    kvkkAcceptedAt: null,
  },

  assignedTrainerId: '',
});

function numOrNull(x) {
  if (x === '' || x == null) return null;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function numOr0(x) {
  if (x === '' || x == null) return 0;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function nOr0(v) {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function intOr0(v) {
  if (v === '' || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

function planSig(p) {
  if (!p) return '';
  const items = Array.isArray(p.items) ? p.items : [];
  const itemsSig = items
    .map((it) => `${it.activityId}:${it.sessions ?? 0}:${it.makeUp ?? 0}:${it.gift ? 1 : 0}`)
    .join('|');
  return `${p.name ?? ''}#${p.priceTry ?? 0}#${p.classCapacity ?? 1}#${itemsSig}`;
}
function normKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase();
}

function mergeUsage(list) {
  const m = new Map();
  for (const x of Array.isArray(list) ? list : []) {
    const name = String(x?.activityName || '').trim();
    if (!name) continue;
    const key = normKey(name);

    const prev = m.get(key) || { activityName: name, usedSessions: 0, usedMakeUp: 0, usedGift: 0 };

    // aynı aktivite gelirse TOPLA (veya max al; ben topluyorum)
    m.set(key, {
      activityName: name, // en güncel isim kalsın
      usedSessions: prev.usedSessions + intOr0(x.usedSessions),
      usedMakeUp: prev.usedMakeUp + intOr0(x.usedMakeUp),
      usedGift: prev.usedGift + intOr0(x.usedGift),
    });
  }
  return Array.from(m.values());
}
function onlyDigits(s) {
  return String(s || '').replace(/\D+/g, '');
}
function toCreateDto(form) {
  const assignedTrainerIdStr = String(form.assignedTrainerId || '').trim();
  const assignedTrainerId = assignedTrainerIdStr === '' ? null : Number(assignedTrainerIdStr);

  return {
    name: String(form.name || '').trim(),
    surname: String(form.surname || '').trim(),

    assignedTrainerId,

    birthDate: form.birthDate ? String(form.birthDate) : null,
    phone: form.phone ? onlyDigits(form.phone) : null,
    notes: form.notes ? String(form.notes).trim() : null,

    planSnapshot: form.planSnapshot ?? null,
    planPriceTry: numOrNull(form.planPriceTry),
    agreedPriceTry: numOrNull(form.agreedPriceTry),

    paidTry: numOr0(form.paidTry),

    membershipStatus: form.membershipStatus === false ? false : true,
    startAt: form.startAt ? String(form.startAt) : null,

    usageByItem: mergeUsage(form.usageByItem),

    communicationConsent: form.communicationConsent
      ? {
          sms: !!form.communicationConsent.sms,
          kvkkAcceptedAt: form.communicationConsent.kvkkAcceptedAt ?? null,
        }
      : { sms: false, kvkkAcceptedAt: null },
  };
}

export function MemberCreateModal({ open, onClose, onSave }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [startAtTouched, setStartAtTouched] = React.useState(false);
  const set = React.useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);
  const reset = React.useCallback(() => {
    setSaving(false);
    setStartAtTouched(false); // ✅
    setForm(emptyForm());
  }, []);
  const usageHasAny = React.useMemo(() => {
    const arr = Array.isArray(form.usageByItem) ? form.usageByItem : [];
    return arr.some(
      (u) => intOr0(u?.usedSessions) > 0 || intOr0(u?.usedMakeUp) > 0 || intOr0(u?.usedGift) > 0
    );
  }, [form.usageByItem]);
  React.useEffect(() => {
    if (usageHasAny && !form.startAt) {
      const today = new Date().toISOString().slice(0, 10);
      set('startAt', today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageHasAny, set]);

  // ✅ Confirm / Warning
  const [warnOpen, setWarnOpen] = React.useState(false);
  const [warnLines, setWarnLines] = React.useState([]); // string[]
  const [warnBusy, setWarnBusy] = React.useState(false);
  const proceedRef = React.useRef(null);

  const closeWarn = React.useCallback(() => {
    if (warnBusy) return;
    setWarnOpen(false);
    setWarnLines([]);
    proceedRef.current = null;
  }, [warnBusy]);

  const openWarn = React.useCallback((lines, onProceed) => {
    setWarnLines(lines);
    proceedRef.current = onProceed;
    setWarnOpen(true);
  }, []);

  // ✅ Borç UI (temiz)

  // ✅ planSnapshot değişince usageByItem scaffold + merge
  const planSnapshotSig = React.useMemo(() => planSig(form.planSnapshot), [form.planSnapshot]);
  const planPriceTry = React.useMemo(() => nOr0(form.planPriceTry), [form.planPriceTry]);
  const agreedTry = React.useMemo(() => nOr0(form.agreedPriceTry), [form.agreedPriceTry]);
  const paidTry = React.useMemo(() => nOr0(form.paidTry), [form.paidTry]);

  const discountPct = React.useMemo(() => {
    if (!planPriceTry || planPriceTry <= 0) return null;
    if (!agreedTry || agreedTry <= 0) return null;
    // plan 4000, agreed 2800 => 30%
    const pct = Math.round(((planPriceTry - agreedTry) / planPriceTry) * 100);
    return pct;
  }, [planPriceTry, agreedTry]);

  React.useEffect(() => {
    const items = Array.isArray(form.planSnapshot?.items) ? form.planSnapshot.items : [];
    if (items.length === 0) {
      if ((form.usageByItem || []).length > 0) set('usageByItem', []);
      return;
    }

    const prev = Array.isArray(form.usageByItem) ? form.usageByItem : [];
    const prevMap = new Map(
      prev.map((u) => [
        String(u.activityName || '')
          .trim()
          .toLowerCase(),
        u,
      ])
    );

    const next = items.map((it) => {
      const key = String(it.activityName || '')
        .trim()
        .toLowerCase();
      const ex = prevMap.get(key);

      return ex
        ? { ...ex, activityName: it.activityName }
        : { activityName: it.activityName, usedSessions: 0, usedMakeUp: 0, usedGift: 0 };
    });

    const same =
      prev.length === next.length &&
      prev.every((p, i) => {
        const n = next[i];
        return (
          String(p.activityName || '')
            .trim()
            .toLowerCase() ===
            String(n.activityName || '')
              .trim()
              .toLowerCase() &&
          Number(p.usedSessions || 0) === Number(n.usedSessions || 0) &&
          Number(p.usedMakeUp || 0) === Number(n.usedMakeUp || 0) &&
          Number(p.usedGift || 0) === Number(n.usedGift || 0)
        );
      });

    if (!same) set('usageByItem', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planSnapshotSig]);

  const canSave =
    String(form.name || '').trim().length >= 2 && String(form.surname || '').trim().length >= 2;

  const safeClose = React.useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [saving, onClose]);

  const doSave = React.useCallback(
    async (dto) => {
      setSaving(true);
      try {
        await onSave?.(dto);

        uiBus.emit('toast', {
          type: 'success',
          message: 'Üye kaydı başarıyla oluşturuldu.',
        });

        reset();
        onClose?.();
      } catch (e) {
        console.error(e);

        uiBus.emit('toast', {
          type: 'error',
          message: e?.message || 'Üye kaydedilirken bir sorun oluştu.',
        });
      } finally {
        setSaving(false);
      }
    },
    [onSave, onClose, reset]
  );

  function isValidTurkishPhone(phone) {
    return /^05\d{9}$/.test(onlyDigits(phone));
  }

  const onClickSave = React.useCallback(async () => {
    if (usageHasAny) setStartAtTouched(true);

    if (usageHasAny && !form.startAt) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Kullanılan hak girildiği için paket başlangıç tarihi gerekli.',
      });
      return;
    }

    const dto = toCreateDto(form);
    console.log('📦 MEMBER CREATE PAYLOAD', dto);

    if (!dto.name || dto.name.length < 2) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Lütfen üyenin adını en az 2 karakter olacak şekilde gir.',
      });
      return;
    }

    if (!dto.surname || dto.surname.length < 2) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Lütfen üyenin soyadını en az 2 karakter olacak şekilde gir.',
      });
      return;
    }

    const phoneDigits = onlyDigits(form.phone);

    if (phoneDigits.length > 0 && !isValidTurkishPhone(phoneDigits)) {
      uiBus.emit('toast', {
        type: 'warning',
        message: 'Telefon numarasını kontrol edelim. Türkiye formatı için 05xx xxx xx xx olmalı.',
      });
      return;
    }

    // ✅ Uyarı kuralları
    const warnings = [];

    // 0) agreed yok ama ödeme var
    if (paidTry > 0 && agreedTry <= 0) {
      warnings.push(`Alınan ödeme (${money(paidTry)}) var ama "Anlaşılan fiyat" boş görünüyor.`);
    }

    // 1) paid > agreed
    if (paidTry > 0 && agreedTry > 0 && paidTry > agreedTry) {
      warnings.push(
        `Alınan ödeme (${money(paidTry)}), anlaşılan fiyattan (${money(
          agreedTry
        )}) yüksek. Fazla tahsilat olabilir.`
      );
    }

    // 2) agreed > planPrice
    if (planPriceTry > 0 && agreedTry > 0 && agreedTry > planPriceTry) {
      warnings.push(
        `Anlaşılan fiyat (${money(agreedTry)}), paket fiyatından (${money(
          planPriceTry
        )}) yüksek. Zam gibi görünüyor.`
      );
    }

    // 3) indirim > %30
    if (typeof discountPct === 'number' && discountPct > 30) {
      warnings.push(`İndirim oranı %${discountPct}. Bu %30'dan fazla. Emin misin?`);
    }

    // ✅ uyarı varsa confirm ile sor
    if (warnings.length > 0) {
      return openWarn(warnings, async () => {
        setWarnBusy(true);
        try {
          await doSave(dto);
        } finally {
          setWarnBusy(false);
          closeWarn();
        }
      });
    }

    // ✅ uyarı yoksa direkt kaydet
    await doSave(dto);
  }, [
    form,
    usageHasAny,
    doSave,
    openWarn,
    closeWarn,
    planPriceTry,
    agreedTry,
    paidTry,
    discountPct,
  ]);

  const debtTry = React.useMemo(() => Math.max(0, agreedTry - paidTry), [agreedTry, paidTry]);
  const startAtMissing = usageHasAny && !String(form.startAt || '').trim();

  const headerBg = 'linear-gradient(180deg, rgba(0,150,136,.16), rgba(0,150,136,.06))';
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : safeClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={isWide ? 'md' : 'sm'}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
          width: isMobile ? '100%' : isWide ? '980px' : '820px',
          maxWidth: isMobile ? '100%' : '95vw',
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <SoftIcon icon="person_add" tone="teal" />
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Yeni Üye
                </MDTypography>

                <MDBox display="flex" gap={1} mt={0.6} sx={{ flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>badge</Icon>}
                    label="Profil"
                  />
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>healing</Icon>}
                    label="Sağlık"
                  />
                  <Chip
                    size="small"
                    icon={<Icon sx={{ fontSize: 16 }}>redeem</Icon>}
                    label="Paket"
                  />
                </MDBox>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={safeClose}
              disabled={saving}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox
          sx={{
            display: 'grid',
            gap: 1.25,
            gridTemplateColumns: { xs: '1fr', lg: '1.15fr .85fr' },
            alignItems: 'start',
          }}
        >
          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <MemberInfoCard form={form} set={set} />
            <PlanPickerCard
              open={open}
              value={form.planRef}
              onChangeValue={(v) => set('planRef', v)}
              onChangePlanPayload={(payload) => {
                setForm((prev) => {
                  const prevSig = planSig(prev.planSnapshot);
                  const nextSig = planSig(payload);
                  if (prevSig === nextSig) return prev;
                  return {
                    ...prev,
                    planSnapshot: payload ?? null,
                    planPriceTry: payload?.priceTry ?? null,
                  };
                });
              }}
              customPlanPayload={form.customPlan}
              onChangeCustomPlanPayload={(payload) => set('customPlan', payload)}
              agreedPriceTry={form.agreedPriceTry}
              onChangeAgreedPriceTry={(v) => set('agreedPriceTry', v)}
            />
            <MembershipUsageCard
              planSnapshot={form.planSnapshot}
              usageByItem={form.usageByItem}
              onChangeUsage={(next) => set('usageByItem', next)}
            />
            {usageHasAny && (
              <FieldCard
                icon="event"
                tone="teal"
                title="Paket Başlangıcı"
                subtitle="Kullanılan hak girildiği için başlangıç tarihi zorunlu"
              >
                <MDBox sx={{ display: 'grid', gap: 0.8 }}>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      opacity: 0.72,
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                    }}
                  >
                    Başlangıç Tarihi (YYYY-MM-DD)
                  </MDTypography>

                  <MDInput
                    type="date"
                    value={form.startAt ?? ''}
                    onChange={(e) => {
                      setStartAtTouched(true);
                      set('startAt', e.target.value);
                    }}
                    onBlur={() => setStartAtTouched(true)}
                    error={startAtTouched && startAtMissing}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '999px',
                        height: { xs: 40, sm: 44 },
                        background: 'rgba(255,255,255,.78)',
                        backdropFilter: 'blur(6px)',
                        '& fieldset': {
                          borderColor:
                            startAtTouched && startAtMissing
                              ? 'rgba(244,67,54,.55)'
                              : 'rgba(0,0,0,.14)',
                        },
                        '&:hover fieldset': {
                          borderColor:
                            startAtTouched && startAtMissing
                              ? 'rgba(244,67,54,.65)'
                              : 'rgba(0,0,0,.26)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor:
                            startAtTouched && startAtMissing
                              ? 'rgba(244,67,54,.75)'
                              : 'rgba(0,150,136,.55)',
                        },
                      },
                      '& input': {
                        fontWeight: 900,
                        color: 'text.primary',
                        fontSize: { xs: 14, sm: 15 },
                        padding: '10px 14px',
                      },
                    }}
                  />

                  {startAtTouched && startAtMissing && (
                    <MDTypography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                      Kullanılan hak girildiği için başlangıç tarihi zorunlu.
                    </MDTypography>
                  )}
                </MDBox>
              </FieldCard>
            )}
          </MDBox>

          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <TrainerSelectCard
              open={open}
              value={form.assignedTrainerId}
              onChangeValue={(v) => set('assignedTrainerId', v)}
            />

            <FieldCard
              icon="payments"
              tone="teal"
              title="Ödeme"
              subtitle="Üye kayıt olurken alınan tutar (alınmadıysa boş bırak)"
            >
              <MDBox
                sx={{
                  display: 'grid',
                  gap: 1.1,
                }}
              >
                {/* label */}
                <MDTypography
                  variant="caption"
                  sx={{
                    color: 'text.primary',
                    opacity: 0.72,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  Alınan Ödeme (₺)
                </MDTypography>

                {/* input */}
                <MDInput
                  type="number"
                  placeholder="0"
                  value={form.paidTry ?? ''}
                  onChange={(e) => set('paidTry', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '999px',
                      height: { xs: 40, sm: 44 },
                      background: 'rgba(255,255,255,.78)',
                      backdropFilter: 'blur(6px)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,.14)' },
                      '&:hover fieldset': { borderColor: 'rgba(0,0,0,.26)' },
                      '&.Mui-focused fieldset': { borderColor: 'rgba(0,150,136,.55)' }, // teal focus
                    },
                    '& input': {
                      fontWeight: 900,
                      color: 'text.primary',
                      fontSize: { xs: 14, sm: 15 },
                      padding: '10px 14px',
                    },
                  }}
                />

                {/* summary row */}
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <MDBox
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.8,
                      px: 1.25,
                      py: 0.7,
                      borderRadius: '999px',
                      border: '1px solid rgba(0,0,0,.10)',
                      background: 'rgba(0,0,0,.03)',
                      width: 'fit-content',
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{ color: 'text.primary', opacity: 0.7, fontWeight: 700 }}
                    >
                      Anlaşılan
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: 'text.primary', fontWeight: 900 }}>
                      {money(agreedTry)}
                    </MDTypography>
                  </MDBox>

                  <MDBox
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.7,
                      px: 1.25,
                      py: 0.7,
                      borderRadius: '999px',
                      border: `1px solid ${
                        debtTry > 0 ? 'rgba(244,67,54,.28)' : 'rgba(76,175,80,.28)'
                      }`,
                      background:
                        debtTry > 0
                          ? 'linear-gradient(180deg, rgba(244,67,54,.10), rgba(244,67,54,.03))'
                          : 'linear-gradient(180deg, rgba(76,175,80,.10), rgba(76,175,80,.03))',
                      width: 'fit-content',
                      justifySelf: { sm: 'end' },
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{ color: 'text.primary', opacity: 0.72, fontWeight: 800 }}
                    >
                      Borç
                    </MDTypography>

                    <MDTypography
                      variant="caption"
                      sx={{
                        fontWeight: 950,
                        color: debtTry > 0 ? 'error.main' : 'success.main',
                      }}
                    >
                      {money(debtTry)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </FieldCard>

            <HealthNotesCard form={form} set={set} />
            <MemberSummaryCard form={form} canSave={canSave} saving={saving} />
          </MDBox>
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={safeClose}
          disabled={saving}
        >
          İptal
        </MDButton>

        <MDBox display="flex" gap={1}>
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px' }}
            onClick={() => {
              if (saving) return;
              reset();
            }}
            disabled={saving}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>refresh</Icon>
            Temizle
          </MDButton>

          <MDButton
            variant="gradient"
            color="success"
            sx={{ borderRadius: '14px' }}
            disabled={!canSave || saving}
            onClick={onClickSave}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
            Oluştur
          </MDButton>
        </MDBox>
      </DialogActions>
      <Dialog
        open={warnOpen}
        onClose={closeWarn}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ fontSize: 18, color: theme.palette.warning.main }}>warning</Icon>
            <MDTypography fontWeight="bold">Kontrol</MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <MDTypography variant="button" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
            Kaydetmeden önce şunları kontrol et:
          </MDTypography>

          <MDBox sx={{ display: 'grid', gap: 0.75 }}>
            {warnLines.map((t, i) => (
              <MDBox key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Icon sx={{ fontSize: 18, color: theme.palette.warning.main, mt: '2px' }}>
                  report
                </Icon>
                <MDTypography variant="button" sx={{ color: 'text.primary' }}>
                  {t}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>

          <MDBox display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
            <MDButton
              variant="outlined"
              color="dark"
              sx={{ borderRadius: '12px' }}
              onClick={closeWarn}
              disabled={warnBusy}
            >
              Düzelt
            </MDButton>

            <MDButton
              variant="gradient"
              color="warning"
              sx={{ borderRadius: '12px' }}
              onClick={() => proceedRef.current?.()}
              disabled={warnBusy}
            >
              <Icon sx={{ mr: 0.5, fontSize: 18 }}>done</Icon>
              Evet, Kaydet
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
